import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
    Output,
    SimpleChanges,
    ViewChild,
    ViewContainerRef
} from '@angular/core';

import { Subscription, filter, takeWhile, BehaviorSubject, combineLatest } from 'rxjs';
import { isEmpty } from 'lodash';

import AgilityEditor from '@agilitypr/ckeditor';
import { Editor, Plugin } from '@ckeditor/ckeditor5-core';
import { Element as CKElement}  from '@ckeditor/ckeditor5-engine';
import { CKEditorComponent } from '@ckeditor/ckeditor5-angular';
import { FileRepository } from '@ckeditor/ckeditor5-upload';

import { DropdownOptionModel } from '../../models/dropdown-option.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { languageDataLocalizationMap } from '../../../../Core/Localization/language-localization.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { DialogIoModel } from '../../../../Core/Dialogs/models/dialog-io.model';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { TranslationModel } from '../../../../Common/Forms/models/translation.model';

import { ControlValueAccessorModel, getProvider } from '../../models/control-value-accessor.model';
import { RichTextEditorConfigModel } from './model/rich-text-editor-config.model';
import { RichTextEditorConfigFeatureEnum } from './enum/rich-text-editor-config-feature.enum';
import { ToolbarStyleHelper } from './helper/toolbar-style.helper';
import { CKColorPickerService } from './service/ck-color-picker.service';
import { CKLinkService } from './service/ck-link.service';
import { RTLHelper } from './helper/rtl.helper';
import { CKUploadService } from './service/ck-upload.service';
import { CKUploadAdapter } from './helper/ck-upload-adapter';
import { CKPersonalizationService } from './service/ck-personalizations.service';
import { CKEditorAccessibilityHelper } from './helper/ck-editor-accessibility.helper';
import { RichTextEditorDefaultStylesModel } from '../../models/rich-text-editor-default-styles.model';
import { ckEditorConfig } from './config/ckeditor-config.model';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { AIRefinementPopupComponent } from '../../../Dialogs/components/AIRefinementPopup/AI-refinement-popup.component';
import { AIRefinementDialogComponent } from '../../../Dialogs/components/AIRefinementDialog/AI-refinement-dialog.component';
import { AIHelperDialogComponent } from '../../../Dialogs/components/AIHelperDialog/AI-helper-dialog.component';
import { translationLanguageOptions } from '../../data/translation-language-options';
import { TranslateService } from '../../services/translate.service';
import { MediaSummaryModel } from '../../../../Common/ContentLibrary/models/content-library-media-summary.model';
import { MessageService } from 'primeng/api';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { ckEditorLocalization } from './config/langauge-config.model';

interface HtmlSelection {
    preSelection: string;
    postSelection: string;
    selection: string;
    ckSelection: any;
    isButton: boolean;
}

@Component({
    selector: 'agpr-rich-text-editor-ck',
    templateUrl: './rich-text-editor-ck.component.html',
    styleUrls: ['./rich-text-editor-ck.component.scss'],
    providers: [
        getProvider(RichTextEditorCkComponent),
        CKColorPickerService,
        CKLinkService,
        CKUploadService,
        CKPersonalizationService
    ],
    standalone: false
})
export class RichTextEditorCkComponent extends ControlValueAccessorModel<string> implements OnInit, OnChanges, OnDestroy, AfterViewInit {
    @ViewChild('AngularEditor') editorComponent: CKEditorComponent;

    @Input() formatOptions: RichTextEditorDefaultStylesModel;
    @Input() richTextEditorConfig: RichTextEditorConfigModel;
    @Input() personalizationOptions: DropdownOptionModel[];
    @Input() isFooter: boolean = false;
    @Input() maxChars: number;
    @Input() addIdToEditableArea: boolean = false;

    @Output() escapeEditor: EventEmitter<null> = new EventEmitter();
    @Output() insertTextBlock: EventEmitter<string> = new EventEmitter();

    public ckEditor = AgilityEditor;
    public watchdog: any;
    public ready = false;

    translation: TranslationModel;
    isTranslating: boolean = false;
    defaultLanguage: DropdownOptionModel;
    defaultLanguages: DropdownOptionModel[] = translationLanguageOptions;
    toolbarElement: HTMLElement;
    languageTag: string;

    public ckEditorConfig = JSON.parse(JSON.stringify(ckEditorConfig));

    overlayContainer: Element;
    openAiPermission: boolean = false;

    private cachedValue: string;
    private toolbarConfigItems = AgilityEditor.defaultConfig.toolbar.items.slice();
    private editorInstance: Editor;
    private resizeObserver: ResizeObserver;

    private afterViewInit: boolean = false;
    private afterEditorInit: boolean = false;

    toolbarHeight: number = 0;
    elementHeight: number = 0;
    elementWidth: number = 0;
    mouseMoveListener: any;
    mousePositionX: number = 0;
    mousePositionY: number = 0;

    private subscriptions: Subscription[] = [];

    acceptedFileTypes: string[] = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

    readonly emptyParagraphMatchText: string[] = ['', '<br/>', '<br>', '<br data-cke-filler="true">'];

    readonly aiPopupWidth: number = 440;
    readonly aiPopupHeight: number = 430;
    readonly offset: number = 30; //  the spacing between the ai refinement pop up and the bottom/right edge of the browser window

    refinementModalOpen: boolean = false;

    constructor(
        public colorPickerService: CKColorPickerService,
        public coreDialogService: CoreDialogService,
        public linkService: CKLinkService,
        public personalizationService: CKPersonalizationService,
        public uploadService: CKUploadService,
        public localizationService: LocalizationService,
        public currentUserStateService: CurrentUserStateService,
        public renderer: Renderer2,
        public injector: Injector,
        public translateService: TranslateService,
        private messageService: MessageService,
        private cd: ChangeDetectorRef,
        private viewContainerRef: ViewContainerRef
    ) {
        super();
    }

    valueChanged(event: any) {
        this.value = event;
        if (this.maxChars && this.value) {
            this.value = this.value.substring(0, this.maxChars);
        }
        if ((this.richTextEditorConfig.enableAIHelper || this.richTextEditorConfig.enableAIRefinement) && this.openAiPermission) {
            this.setupRefinementHover();
        }
        this.onModelChange(this.value);
        this.cd.detectChanges();
    }

    writeValue(value: string) {
        super.writeValue(value);
        this.cachedValue = value;
        this.cd.detectChanges();
    }

    ngOnInit() {
        this.subscriptions.push(
            this.currentUserStateService.getPermissions().pipe(filter(x => !!x)).subscribe(permissions => {
                if (permissions?.length) {
                    this.openAiPermission =  permissions.includes(userPermissionsEnum.openAI);
                    // If they don't have permission, and the config was left on by the init, remove it now
                    if (!this.openAiPermission && this.richTextEditorConfig.enableAIHelper) {
                        this.ckEditorConfig.agprPrCoPilot.showAiHelper = false;
                    }
                    if (!this.openAiPermission && this.richTextEditorConfig.enableAIRefinement) {
                        this.removeFeature(RichTextEditorConfigFeatureEnum.enableAIRefinement);
                        this.ckEditorConfig.agprPrCoPilot.showPrCoPilotBalloon = false;
                    }
                }
            }),
            this.currentUserStateService.getUILanguageTag().subscribe(tag => {
                this.defaultLanguages = this.defaultLanguages.map(option => {
                    return {
                        value: option.value,
                        label: languageDataLocalizationMap[tag + '.' + option.subLabel],
                        subLabel: option.subLabel
                    };
                });
                this.ckEditorConfig.translationLanguages = this.defaultLanguages;
                if (this.defaultLanguage) {
                    this.defaultLanguage = this.defaultLanguages.find(option => option.subLabel === this.defaultLanguage.subLabel);
                } else {
                    this.defaultLanguage = this.defaultLanguages.find(option => option.subLabel === tag);
                }
                this.addlocalization(tag, ckEditorLocalization);
                this.ckEditorConfig.language.ui = tag;
                this.ckEditorConfig.language.textPartLanguage.forEach(lang => lang.title = languageDataLocalizationMap[tag + '.' + lang.languageCode]);
            })
        );

        this.overlayContainer = document.getElementById('main-content');

        if (this.richTextEditorConfig) {
            if (!this.richTextEditorConfig.enablePersonalizations) {
                this.removeFeature(RichTextEditorConfigFeatureEnum.enablePersonalizations);
            }
            if (!this.richTextEditorConfig.enableLineHeight) {
                this.removeFeature(RichTextEditorConfigFeatureEnum.enableLineHeight);
            }
            if (!this.richTextEditorConfig.enableHeading) {
                this.removeFeature(RichTextEditorConfigFeatureEnum.enableHeading);
            }
            if (!this.richTextEditorConfig.enableFontColor) {
                this.removeFeature(RichTextEditorConfigFeatureEnum.enableFontColor);
            }
            if (!this.richTextEditorConfig.enableBackgroundColor) {
                this.removeFeature(RichTextEditorConfigFeatureEnum.enableBackgroundColor);
            }
            if (!this.richTextEditorConfig.enableHtmlEditor) {
                this.removeFeature(RichTextEditorConfigFeatureEnum.enableHtmlEditor);
            }
            if (!this.richTextEditorConfig.enableAIHelper) {
                this.ckEditorConfig.agprPrCoPilot.showAiHelper = false;
            }
            if (!this.richTextEditorConfig.enableAIRefinement) {
                this.removeFeature(RichTextEditorConfigFeatureEnum.enableAIRefinement);
                this.ckEditorConfig.agprPrCoPilot.showPrCoPilotBalloon = false;
            }
            if (!this.richTextEditorConfig.enableFontFamily) {
                this.removeFeature(RichTextEditorConfigFeatureEnum.enableFontFamily);
            }
            if (!this.richTextEditorConfig.enableFontSize) {
                this.removeFeature(RichTextEditorConfigFeatureEnum.enableFontSize);
            }
            if (!this.richTextEditorConfig.enableAlignment) {
                this.removeFeature(RichTextEditorConfigFeatureEnum.enableAlignment);
            }
            if (!this.richTextEditorConfig.enableInsertImage) {
                this.removeFeature(RichTextEditorConfigFeatureEnum.enableInsertImage);
            }
            if (!this.richTextEditorConfig.enableStrikethrough) {
                this.removeFeature(RichTextEditorConfigFeatureEnum.enableStrikethrough);
            }
            if (!this.richTextEditorConfig.enableNumberedList) {
                this.removeFeature(RichTextEditorConfigFeatureEnum.enableNumberedList);
            }
            if (!this.richTextEditorConfig.enableBlockQuote) {
                this.removeFeature(RichTextEditorConfigFeatureEnum.enableBlockQuote);
            }
            if (!this.richTextEditorConfig.enableTranslation) {
                this.removeFeature(RichTextEditorConfigFeatureEnum.enableTranslation);
            } else {
                this.translation = new TranslationModel();
            }
            if (!this.richTextEditorConfig.enableDirection) {
                const index = this.toolbarConfigItems.findIndex(x => x.icon === 'threeVerticalDots');
                if (index > -1) {
                    this.toolbarConfigItems.splice(index, 1);
                }
            }
            if (!this.richTextEditorConfig.enableIndent) {
                this.toolbarConfigItems.splice(this.toolbarConfigItems.indexOf('indent'), 1);
                this.toolbarConfigItems.splice(this.toolbarConfigItems.indexOf('outdent'), 1);
            }
        }
        // remove all repeated separators so that toolbarConfigItems can be mapped to editor.ui.view.toolbar.items
        for (let i = 0; i < this.toolbarConfigItems.length; i++) {
            const item = this.toolbarConfigItems[i];
            if (item === '|') {
                if (this.toolbarConfigItems[i + 1] === '|') {
                    this.toolbarConfigItems.splice(i, 1);
                    i--;
                }
            }
            if (item === '-' && i === this.toolbarConfigItems.length - 1) {
                this.toolbarConfigItems.splice(i);
            }
        }
        this.ckEditorConfig.toolbar = {
            items: this.toolbarConfigItems
        };
        if (this.richTextEditorConfig?.enablePersonalizations) {
            this.ckEditorConfig.personalizations = this.personalizationService.setUpOptions(this.personalizationOptions);
            this.personalizationService.setIsFooter(this.isFooter);
        } else {
            this.ckEditorConfig.personalizations = [];
        }
    }

    private removeFeature(feature: RichTextEditorConfigFeatureEnum) {
        for (const key of RichTextEditorConfigModel.FEATURE_MAP.get(feature)) {
            this.toolbarConfigItems.splice(
                this.toolbarConfigItems.indexOf(key),
                1
            );
        }
    }

    public initEditor(editor: any) {
        if (!editor) {
            return;
        }
        this.editorInstance = editor;

        this.colorPickerService.setUpColorPickers(this.editorInstance, this.viewContainerRef);
        this.linkService.setUpLinkDialog(this.editorInstance);
        this.personalizationService.handleItemsClicked(this.editorInstance);
        ToolbarStyleHelper.applyStyleChange(this.editorInstance, this.toolbarConfigItems, this.localizationService);
        RTLHelper.applyRTLFix(editor);
        CKEditorAccessibilityHelper.applyAccessibilityFix(this.editorInstance, this.toolbarConfigItems, this.escapeTextEditor.bind(this));


        const sourceEditing = this.editorInstance.plugins.get('SourceEditing') as any;
        this.applyRHPStyles(this.formatOptions, sourceEditing.isSourceEditingMode);
        sourceEditing.on('change', event => {
            this.applyRHPStyles(this.formatOptions, event.source.isSourceEditingMode);
        });

        (this.editorInstance.plugins.get('FileRepository') as FileRepository).createUploadAdapter = (loader) => {
            return new CKUploadAdapter(loader, this.uploadService);
        };

        if ((this.richTextEditorConfig.enableAIHelper || this.richTextEditorConfig.enableAIRefinement) && this.openAiPermission) {
            this.setupRefinementHover();
        }

        this.subscriptions.push(
            combineLatest([
                this.currentUserStateService.getDataReferenceLanguageTag(),
                this.currentUserStateService.getUILanguageTag()
            ]).subscribe(([refLang, uiLang]) => {
                let languageString = 'English (US)';
                if (languageDataLocalizationMap[uiLang + '.' + refLang]) {
                    languageString = languageDataLocalizationMap[uiLang + '.' + refLang];
                }
                editor.fire('translationLanguageChanged', languageString);
            })
        );

        (editor.plugins.get('agprTranslationButtonUI') as Plugin)?.on('translateLanguage', (event, language) => {
            this.translation.originalContent = this.value;
            this.translation.setIsTranslating(true);

            if (isEmpty(language)) {
                language = this.defaultLanguage;
            } else {
                language = this.defaultLanguages.find(defaultLanguage => defaultLanguage.label === language);
            }

            this.translateService.setupChatAIForText(this.translation, language);

            this.translation.isTranslating$.pipe(
                takeWhile(isTranslating => {
                    this.value = this.translation.translatedContent;
                    this.change();

                    this.isTranslating = isTranslating;
                    return isTranslating;
                })
            ).subscribe();
        });

        (editor.plugins.get('agprPrCoPilotUI') as Plugin)?.on('coPilotFired', (event, selections) => {
            if (this.value.length === 0 && this.richTextEditorConfig.enableAIHelper) {
                const ioModel = this.getAIHelperConfig();
                this.coreDialogService.open(AIHelperDialogComponent, ioModel, this.injector);
            } else {
                const source = selections.isButton ? (event.source as any).toolbarButtonView.element : (event.source as any).balloon.view.element;
                this.launchAIRefinementDialog(selections, source);
            }
        });

        this.editorInstance.editing.view.focus();

        this.afterEditorInit = true;
        this.startObserver();

        const element = editor.ui.getEditableElement()?.closest('.agpr-layoutBlock-container');
        if (element) {
            this.toolbarElement = editor.ui.view.toolbar.element;
            const parent = element.parentElement;
            parent.insertBefore(
                this.toolbarElement,
                element
            );
            this.toolbarElement.style.zIndex = '3';
            this.toolbarElement.addEventListener('click', (event) => {
                event.stopPropagation();
            });
        }
        // Disable all CKEditor notifications (warning, success, error)
        const notification = editor.plugins.get('Notification');
        notification.showWarning = () => {};
        notification.showSuccess = () => {};
        notification.showError = () => {};

        const toolbarElements = editor.ui.view.toolbar.element;
        toolbarElements.addEventListener('click', (event: Event) => {
            const target = event.target as HTMLElement;

            // Find the "Insert image" button by its tooltip text attribute
            const button = target.closest('[data-cke-tooltip-text="Insert image"]') as HTMLElement;
            if (button) {
                // Prevent CKEditor's default file upload dialog from opening
                event.preventDefault();
                event.stopImmediatePropagation();
                // Instead, open the custom Content Library dialog to select/upload images
                this.openContentLibrary();
            }
        }, true);


        if (this.addIdToEditableArea) {
            editor.editing.view.change(writer => {
                const viewRoot = editor.editing.view.document.getRoot();
                writer.setAttribute('id', 'agpr_richTextEditor_element_editable', viewRoot);
            });
        }
        this.cd.detectChanges();
    }

    /**
     * Opens the Content Library dialog, allowing to select or upload an image.
     * Once the dialog is closed with a valid selection, the chosen image is inserted
     * into the CKEditor instance at the current cursor position.
     */
    openContentLibrary(): void {
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io = {
            inputs: {
                animationTrigger,
                overlayContainer,
                selectionMode: true,
                singleSelect: true,
                acceptedFileTypes: this.acceptedFileTypes,
                navBarLaunch: false
            },
            outputs: {
                dialogClose: (selectedFiles: MediaSummaryModel[]) => {
                    if (!selectedFiles?.length) {
                        return;
                    }

                    const invalidFiles = selectedFiles.filter(file =>
                        !this.acceptedFileTypes.includes(file.mimeType)
                    );

                    if (invalidFiles.length > 0) {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: this.localizationService.transform('PageBuilder.error.fileTypeHeader'),
                            detail: this.localizationService.transform('PageBuilder.error.fileTypeMessage', {
                                types: this.acceptedFileTypes.map(type => type.replace('image/', '')).join(', ')
                            })
                        });
                        return;
                    }

                    // If a valid image is selected → insert it into the editor
                    if (selectedFiles[0]?.url && this.editorInstance) {
                        const imageUrl = selectedFiles[0].url;

                        // Use CKEditor's insertImage command to insert the image at the current cursor position
                        this.editorInstance.execute('insertImage', {
                            source: imageUrl
                        });

                        // Refocus the editor to allow the user to continue editing
                        this.editorInstance.editing.view.focus();
                    }
                }
            }
        };
        this.coreDialogService.open('ManageContentLibraryDialogComponent', io);
    }

    /**
     * Gets returns the html that is selected
     */
    getSelectionsHtml(): HtmlSelection {
        return {
            preSelection: '',
            postSelection: '',
            selection: '',
            ckSelection: null,
            isButton: false
        };
    }

    launchAIRefinementDialog(selections: HtmlSelection, balloonElement: any) {
        const ioModel = this.getAIRefinementConfig(selections, balloonElement);
        this.refinementModalOpen = true;
        if (balloonElement) {
            this.coreDialogService.open(AIRefinementPopupComponent, ioModel, this.injector, 'AIRefinementPopup');
        } else {
            const aiRefinementDialogKey = this.coreDialogService.open(AIRefinementDialogComponent, ioModel, this.injector);
            this.coreDialogService.getDialog(aiRefinementDialogKey).changeDetectorRef.detectChanges();
        }
    }

    private getAIHelperConfig(): DialogIoModel {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const ioModel: DialogIoModel = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                title: 'Common.dialog.aiHelper.title',
                aiReferenceId: this.richTextEditorConfig.aiReferenceId
            },
            outputs: {
                dialogClose: (text: string) => {
                    if (text) {
                        text = text.replace(/(\n|\n\r)/g, '<br/>');
                        this.value = text;
                        this.change();
                    }
                }
            }
        };

        return ioModel;
    }

    private getAIRefinementConfig(selections: HtmlSelection, balloonElement?: any): DialogIoModel {
        let isSelectAll = false;

        // If the button in the toolbar was pressed, check to see if there was anything selected, if not, select everything
        if (selections.isButton) {
            if (selections.selection.length === 0) {
                // We need to combine pre and post selection, because the location of the user's cursor is still the split point between pre and post selection
                isSelectAll = true;
                selections.selection = selections.preSelection.trim() + ' ' + selections.postSelection;
                selections.preSelection = '';
                selections.postSelection = '';
            }
        }

        const positionX = Math.min(balloonElement?.getBoundingClientRect()?.x || 0, window.innerWidth - this.aiPopupWidth - this.offset);
        // we don't want to popup window to go outside
        const positionY = Math.min(balloonElement?.getBoundingClientRect()?.y || 0, window.innerHeight - this.aiPopupHeight - this.offset);

        // Get the editors selection, need to do this to get a snapshot of what it is, if we don't get the ranges then they'll change when the selection changes
        const agilityCkSelection = this.editorInstance.model.document.selection;
        const agilityCkSelectionRanges = Array.from(agilityCkSelection.getRanges());

        const ioModel: DialogIoModel = {
            inputs: {
                overlayContainer: this.overlayContainer,
                selectionHtml: selections.selection,
                preSelectionHtml: selections.preSelection,
                postSelectionHtml: selections.postSelection,
                aiReferenceId: this.richTextEditorConfig.aiReferenceId,
                style: AgprComponentStyleEnum.social,
                positionX: positionX,
                positionY: positionY,
                hideInsert: this.richTextEditorConfig.hideInsert
            },
            outputs: {
                dialogClose: (action: any) => {
                    const model = this.editorInstance.model;
                    const data = this.editorInstance.data;

                    if (action) {
                        if (action.action === 'replace') {
                            if (isSelectAll) {
                                this.value = action.generatedContent;
                            } else {
                                this.editorInstance.model.change((writer) => {
                                    const newSelection = writer.createSelection(agilityCkSelectionRanges);
                                    writer.setSelection(newSelection);
                                    const viewFragment = data.processor.toView(action.generatedContent);
                                    const modelFragment = data.toModel(viewFragment);
                                    model.insertContent(modelFragment);
                                });
                            }
                        } else if (action.action === 'insert') {
                            this.insertTextBlock.emit(action.generatedContent);
                        }
                        this.change();
                    }
                    this.refinementModalOpen = false;
                }
            }
        };

        return ioModel;
    }

    change() {
        this.onModelChange(this.value);
    }

    escapeTextEditor = () => this.escapeEditor.emit();

    ngAfterViewInit() {
        this.afterViewInit = true;
        this.startObserver();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasOwnProperty('formatOptions') && this.editorInstance) {
            const options = changes.formatOptions.currentValue;
            this.applyRHPStyles(options, (this.editorInstance.plugins.get('SourceEditing') as any)?.isSourceEditingMode);
        }
    }

    startObserver(): void {
        if (!this.afterEditorInit || !this.afterViewInit) {
            return;
        }
        this.resizeObserver = new ResizeObserver(entry => ToolbarStyleHelper.adjustMoreOptionDropdownPanelPosition(
            this.editorInstance, this.toolbarConfigItems
        ));
        try {
            this.resizeObserver.observe(document.querySelector('.ck-editor'));
        } catch (error) {
            // sometimes .ck-editor doesn't return an element, we may need to handle this case,
            // but it appears to not cause issues in the cases the error is thrown
        }
    }

    // Apply form field styles to text editor editing mode, but not html mode
    // This is called when: 1. initEditor 2. switching between modes 3. change form field values
    applyRHPStyles(options: any, isSourceEditing: boolean): void {
        const textarea = document.querySelector('.ck-editor__main') as HTMLElement;
        if (options && textarea) {
            if (!isSourceEditing) {
                textarea.style.fontFamily = options['font-family'];
                textarea.style.fontWeight = options['font-weight'] === 'bold' ? '700' : '400';
                textarea.style.fontStyle = options['font-style'] === 'italic' ? 'italic' : 'normal';
                textarea.style.textDecoration = options['text-decoration'] === 'underline' ? 'underline' : 'none';
                textarea.style.fontSize = options['fontSize'] || options['font-size'];
                textarea.style.textAlign = options['text-align'];
                textarea.style.color = options['color'];
                textarea.style.backgroundColor = options['background-color'];
                textarea.style.paddingTop = options['padding-top'] + 'px';
                textarea.style.paddingRight = options['padding-right'] + 'px';
                textarea.style.paddingBottom = options['padding-bottom'] + 'px';
                textarea.style.paddingLeft = options['padding-left'] + 'px';
            } else {
                textarea.style.fontFamily = null;
                textarea.style.fontWeight = null;
                textarea.style.fontStyle = null;
                textarea.style.textDecoration = null;
                textarea.style.fontSize = null;
                textarea.style.textAlign = null;
                textarea.style.color = null;
                textarea.style.backgroundColor = null;
                textarea.style.paddingTop = null;
                textarea.style.paddingRight = null;
                textarea.style.paddingBottom = null;
                textarea.style.paddingLeft = null;
            }
        }
    }


    ngOnDestroy() {
        this.subscriptions.forEach(x => x.unsubscribe());
        if (this.mouseMoveListener) {
            this.mouseMoveListener();
            this.mouseMoveListener = null;
        }

        const element = document.querySelector('.ck-editor');
        if (element) {
            this.resizeObserver?.unobserve(element);
        }

        this.colorPickerService.destory();
        this.toolbarElement?.removeEventListener('click', (event) => event.stopPropagation());
        this.toolbarElement?.remove();
    }

    setupRefinementHover() {
        requestAnimationFrame(() => {
            const editorElement = document.getElementById('agpr_richTextEditor_element');
            if (!editorElement) {
                return;
            }
            // gettings the toolbar heights & editor heights & with to make sure it doesn't go outside the "box"
            this.toolbarHeight = document.querySelector('.ck-editor__top')?.clientHeight || 0;
            this.elementHeight = editorElement.clientHeight || 0;
            this.elementWidth = editorElement.clientWidth || 0;

            // remove all existing listeners
            if (this.mouseMoveListener) {
                this.mouseMoveListener();
            }

            // we need to add mouse listener to know where the last position of the mouse was after text select
            this.mouseMoveListener = this.renderer?.listen(document.getElementById('agpr_richTextEditor_element'), 'mousemove', (mouseMoveEvent) => {
                this.mousePositionX = mouseMoveEvent.layerX;
                this.mousePositionY = mouseMoveEvent.layerY;
            });

            // get all the paragraphs in the rich editor
            const blockContents = Array.from(document.querySelectorAll(
                '.ck-content > p, .ck-content > h1, .ck-content > h2, .ck-content > h3, .ck-content > ol, .ck-content > ul'
            )) as HTMLElement[];

            for(let i = 0; i < blockContents.length; i++) {
                blockContents[i].onmouseenter = (hoverEvent: any) => {
                    if (!document.getElementById('agpr_richTextEditor_element')) {
                        return;
                    }
                    // remove other paragraph buttons
                    this.clearRefineParagraphButton();

                    // if the block content actually has text in it
                    if (!this.emptyParagraphMatchText.includes(blockContents[i]?.innerHTML?.trim())) {
                        const aiButton = document.createElement('button');
                        aiButton.id = 'agpr_richTextEditor_refinementParagraph';
                        aiButton.className = 'agpr-richTextEditor-refinementParagraph';

                        aiButton.style.position = 'absolute';
                        aiButton.style.top = hoverEvent?.srcElement?.closest('p,h1,h2,h3,ol,ul')?.['offsetTop'] + 'px';
                        aiButton.style.left = '-18px';

                        aiButton.onmouseenter = () => {
                            blockContents[i]?.classList.add('agpr-richTextEditor-highlightParagraph');
                        };

                        aiButton.onmouseout = () => {
                            this.removeHighlightParagraph();
                        };

                        aiButton.onclick = () => {
                            const root = this.editorInstance.model.document.getRoots()[0];
                            const node: CKElement = this.traverseNodeForText(blockContents[i].innerText, root);
                            if (node) {
                                // Set the selection
                                this.editorInstance.editing.view.focus();
                                this.editorInstance.model.change((writer) => {
                                    writer.setSelection(node, 'on');
                                    writer.model.getSelectedContent(writer.model.document.selection);
                                });

                                // Get the content and open the popup
                                const selections: HtmlSelection = (this.editorInstance.plugins.get('agprPrCoPilotUI') as any)?.getSelectionsHtml();
                                this.launchAIRefinementDialog(selections, aiButton);
                            }
                        };

                        const buttonTextEl = document.createElement('span');
                        buttonTextEl.className = 'fa-kit-duotone fa-agpr-ai';
                        aiButton.appendChild(buttonTextEl);
                        document.getElementById('agpr_richTextEditor_element').appendChild(aiButton);

                    }
                };
            }
        });
    }

    private traverseNodeForText(text, node): CKElement {
        let returnNode: CKElement = null;

        if (node.data?.trim() === text.trim()) {
            return node;
        }

        if (node.childCount) {
            for (const child of node.getChildren()) {
                returnNode = this.traverseNodeForText(text, child);
                if (returnNode !== null) {
                    return returnNode;
                }
            }
        }

        return returnNode;
    }

    clearRefineParagraphButton(): void {
        document.getElementById('agpr_richTextEditor_refinementParagraph')?.remove();
    }

    removeHighlightParagraph() {
        const els = document.getElementsByClassName('agpr-richTextEditor-highlightParagraph');

        // remove all other agpr-richTextEditor-highlightParagraph classes
        Array.from(els).forEach((el) => {
            el.classList.remove('agpr-richTextEditor-highlightParagraph');
        });
    }

    addlocalization(language: string, translations: any): void {
        Object.entries(translations).forEach(([k, v]) => {
            translations[k] = this.localizationService.transform(v as string);
        });

        if (!global.window.CKEDITOR_TRANSLATIONS) {
            global.window.CKEDITOR_TRANSLATIONS = {};
        }

        if (!global.window.CKEDITOR_TRANSLATIONS[language]) {
            global.window.CKEDITOR_TRANSLATIONS[language] = { dictionary: null, getPluralForm: null };
        }

        const languageTranslations = global.window.CKEDITOR_TRANSLATIONS[language];

        languageTranslations.dictionary = languageTranslations.dictionary || {};
        languageTranslations.getPluralForm = languageTranslations.getPluralForm;

        // Extend the dictionary for the given language.
        Object.assign(languageTranslations.dictionary, translations);
    }
}
